/**
 * SellerBillingGate — second gate (after SellerSetupGate). Forces an
 * unsubscribed seller through `/seller/onboarding/plan` before any
 * non-billing route can render. This is the "after KYB, mandatory
 * plan picker" requirement from the SaaS billing spec.
 *
 * Allowed regardless of subscription state:
 *   • /seller/onboarding/plan   (the paywall page itself)
 *   • /seller/settings          (Plan & Billing lives here)
 *   • /seller/wallet            (top up to fund subscription)
 *
 * If billing.status is anything other than `unsubscribed`, the gate
 * is a no-op. Banner / write-gate middleware handle grace/read-only/
 * suspended states elsewhere.
 */
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useSellerBilling } from "../context/SellerBillingContext";


const ALLOWED_PREFIXES = [
  "/seller/onboarding/plan",
  "/seller/settings",
  "/seller/wallet",
];


export default function SellerBillingGate({ children }) {
  const { status, loading } = useSellerBilling();
  const location = useLocation();

  if (loading && !status) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-foreground/40">
        <Loader2 className="w-5 h-5 animate-spin"/>
      </div>
    );
  }
  if (!status) return children;
  // Master kill switch — when SaaS billing is dormant (pre-launch), the
  // signup paywall doesn't fire. Sellers go straight to the dashboard.
  if (status.enforced === false) return children;
  if (status.status !== "unsubscribed") return children;

  // Allow billing-recovery routes so the user can actually subscribe.
  if (ALLOWED_PREFIXES.some(p => location.pathname.startsWith(p))) {
    return children;
  }
  return <Navigate to="/seller/onboarding/plan" replace state={{ from: location.pathname }}/>;
}
